import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useHistory, useParams} from "react-router";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Tagline} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    ConversionContextProvider,
    enums,
    publishAnalyticsEventForMinorConversion,
    useMarkConversion,
} from "@khan/analytics-learning-time";
import {useQuery} from "@khan/data-gql";
import {setCoeditorMatchedTrigger} from "@khan/guide-coeditor-core";
import {
    publishAiGuideNavigationEvent,
    useGuideAudioController,
    useResetConversation,
    useSendChatMessage,
} from "@khan/guide-core";
import {isIFrame, useAIGuideToolPersona} from "@khan/guide-core-helpers";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {
    Coeditor,
    CoeditorContextProvider,
    GuideDisclaimer,
    type AutosavingStatus,
} from "@khan/guide-ui";
import {BlooketStaging, type QuizContent} from "@khan/khanmigo-tools/blooket";
import type {KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";
import {randomUUID} from "@khan/util-random-uuid";

import PageRoot from "#app-shell-package/page-root.tsx";
import EmbeddedPageTemplate from "#app-shell-package/page-templates/embedded-page-template.tsx";
import HeaderOnlyPageTemplate from "#app-shell-package/page-templates/header-only-page-template.tsx";
import DelayedRedirect from "#app-shell-package/redirects/delayed-redirect.tsx";
import {ADMIN_TOOLS_FETCH} from "#districts-package/admin-ai-guide-activities/ai-admin-tools-query.ts";
import PrefsDrawer from "#guide-activities/ui/prefs-drawer.tsx";
import {PARENT_KHANMIGO_TOOLS_PATH} from "#page-package/util.ts";
import {PARENT_TOOLS_FETCH} from "#parent-dashboard-package/graphql/ai-tools-query.ts";

import AIToolHeader from "./ai-tool-header.tsx";
import {
    getAdminToolsByUrlSlug,
    getParentToolsByUrlSlug,
    getToolsByUrlSlug,
    Layout,
    type Tool,
} from "./ai-tools-list.ts";
import {TEACHER_TOOLS_FETCH} from "./ai-tools-queries.ts";
import ChatOnlyTool from "./chat-only-tool.tsx";
import ChatSidebar from "./chat-sidebar.tsx";
import DocumentCopiedToast from "./document-copied-toast.tsx";
import EditorTutorial from "./editor-tutorial.tsx";
import GuideTermsModal from "./guide-terms-modal.tsx";
import {withStrings} from "./locales/ai-tool-page/load-strings.tsx";
import {
    ADMIN_KHANMIGO_TOOLS_PATH,
    TEACHER_KHANMIGO_TOOLS_PATH,
} from "./path-helpers.ts";
import UserInputs from "./user-inputs.tsx";
import {getToolId, Platform} from "./utils.ts";

type AIToolPageWrapperProps = {
    params: {tool: string};
    platform: Platform;
    search: string;
};

type AIToolPageProps = AIToolPageWrapperProps & {
    tool: Tool;
    isLoading: boolean;
    isSuccess: boolean;
};

const AIToolPage = ({
    platform,
    search,
    isLoading,
    isSuccess,
    tool,
}: AIToolPageProps): React.ReactElement | null => {
    const [showPreferences, setShowPreferences] = React.useState(false);

    const history = useHistory();
    const conversation = useGuideState("conversation");
    const threadId = useGuideState("threadId");
    const navigation = useGuideState("navigation");

    const sendChatMessage = useSendChatMessage();
    const resetConversation = useResetConversation();
    const {isVoiceOn, selectedVoice} = useGuideAudioController();

    const [showChat, setShowChat] = React.useState(false);
    const [sendingFirstMessage, setSendingFirstMessage] = React.useState(false);
    const [errorSubmitting, setErrorSubmitting] = React.useState<
        KAError | undefined
    >(undefined);
    const [mustProvideInputs, setMustProvideInputs] = React.useState(() => {
        // We hide the input form if the URL indicates we're resuming a thread;
        // we assume that the necessary prompt variables have been saved and
        // will be restored elsewhere for this thread to continue.
        const isResuming = new URLSearchParams(search).has("resumeThreadId");
        return isResuming ? false : true;
    });
    const markConversion = useMarkConversion();
    const persona = useAIGuideToolPersona();

    const [savingStatus, setSavingStatus] =
        React.useState<AutosavingStatus>("idle");

    const [redirectURL, setRedirectURL] = React.useState("");
    const [questionSetContent, setQuestionSetContent] =
        React.useState<QuizContent>();

    React.useEffect(() => {
        if (showChat) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorChatOpened",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
            });
        }
    }, [showChat, threadId, navigation]);

    React.useEffect(() => {
        // Set the activity persona so it can be used by AI tool UI elements.
        // For example, the at-icon-button.tsx uses it to determine tooltip copy.
        GuideStateApi.set("activityPersona", persona);

        return function cleanup() {
            GuideStateApi.set("activityPersona", null);
        };
    }, [persona]);

    React.useEffect(() => {
        if (platform === Platform.Canvas) {
            const pathname = history.location.pathname;
            const newNavigationId = randomUUID();
            publishAiGuideNavigationEvent({
                pathname,
                newNavigationId,
                product: "INSTRUCTURE_CANVAS",
            });
        }
    }, [platform, history]);

    React.useEffect(() => {
        markConversion({
            id: "pageview_khanmigo_tool",
        });
    }, [markConversion]);

    if (isLoading) {
        return <CircularSpinner />;
    }

    const {requireClassroomSelection} = tool;
    const chatOnly = tool.layout === Layout.ChatOnly;

    // The inputs we show in the form are taken from the first Khanmigo-sent
    // message that has inputs on it:
    const incomingMessageWithInputs = conversation.find(
        (entry) =>
            entry.user === "Teacher" &&
            entry.requestedInputs &&
            entry.requestedInputs.length > 0,
    );

    const primaryButtonText = t`Start`;

    const resetDocumentAndChat = () => {
        setSendingFirstMessage(false);
        setErrorSubmitting(undefined);
        setMustProvideInputs(true);
        setShowChat(false);
        resetConversation(true);
        setSavingStatus("idle");
    };

    const userInputs = (
        <UserInputs
            tool={tool}
            submitText={primaryButtonText}
            loading={!incomingMessageWithInputs}
            inputs={incomingMessageWithInputs?.requestedInputs || []}
            submitting={sendingFirstMessage}
            submitError={errorSubmitting}
            onSubmit={async () => {
                setErrorSubmitting(undefined);
                setSendingFirstMessage(true);
                setMustProvideInputs(false);

                setCoeditorMatchedTrigger(true);

                await sendChatMessage(
                    primaryButtonText,
                    {
                        containsSpeechToTextContent: false,
                        guideVoiceDescriptor: isVoiceOn ? selectedVoice : null,
                        requestUIType: "ACTION_BUBBLE",
                        prepopulatedText: primaryButtonText,
                        requestWithTextToSpeech: isVoiceOn,
                        timeToSubmit: 0,
                    },
                    [],
                    {
                        onResponseStart: () => {
                            setSendingFirstMessage(false);
                        },
                        onResponseFailed: (err: KAError) => {
                            setErrorSubmitting(err);
                            setSendingFirstMessage(false);
                            setMustProvideInputs(true);
                            setCoeditorMatchedTrigger(false);
                        },
                    },
                );
            }}
        />
    );

    const getInputsAndDisplayLayout = (node: React.ReactNode) => {
        if (mustProvideInputs) {
            return userInputs;
        }
        if (sendingFirstMessage) {
            return (
                <View style={styles.loading}>
                    <CircularSpinner size="medium" />
                </View>
            );
        }
        return node;
    };

    const getLayout = (layout: Layout) => {
        switch (layout) {
            case Layout.Blooket:
                return getInputsAndDisplayLayout(
                    <BlooketStaging
                        persona={persona}
                        setSavingStatus={setSavingStatus}
                        setQuestionSetContent={setQuestionSetContent}
                        searchParams={search}
                    />,
                );
            case Layout.ChatOnly:
                return (
                    <ChatOnlyTool
                        requireClassroomSelection={requireClassroomSelection}
                    />
                );
            case Layout.Coeditor:
                return getInputsAndDisplayLayout(
                    <View style={styles.coeditorWrapper}>
                        <EditorTutorial />
                        <Coeditor
                            onChatSuggestionClicked={() => setShowChat(true)}
                            setSavingStatus={setSavingStatus}
                        />
                        <DocumentCopiedToast />
                    </View>,
                );
        }
    };
    const mainContent = getLayout(tool.layout);

    // TODO: Might want to add a link back to the Khanmigo Tools tab, but would
    // need to get a string translated.
    const displayError = (
        <View>
            <Tagline>{t`Sorry, an unexpected error occurred.`}</Tagline>
        </View>
    );

    const aiToolContent = (
        <View style={styles.panel}>
            {!isSuccess && displayError}
            {isSuccess && (
                <PrefsDrawer
                    open={showPreferences}
                    onClose={() => setShowPreferences(false)}
                >
                    <AIToolHeader
                        tool={tool}
                        resetDocumentAndChat={resetDocumentAndChat}
                        onSelectPreferences={() => setShowPreferences(true)}
                        savingStatus={savingStatus}
                        platform={platform}
                        isProvidingInputs={mustProvideInputs}
                        handleBlooketRedirect={setRedirectURL}
                        questionSetContent={questionSetContent}
                    />
                    <View style={styles.wrapper}>
                        <View style={styles.column}>
                            <GuideDisclaimer style={styles.disclaimer} />
                            <View
                                style={[
                                    styles.mainContent,
                                    tool.layout !== Layout.Blooket &&
                                        styles.mainContentPaintedContainer,
                                    chatOnly &&
                                        (isIFrame()
                                            ? styles.chatOnlyMainContentIframe
                                            : styles.chatOnlyMainContent),
                                ]}
                            >
                                {mainContent}
                            </View>
                        </View>
                        {!chatOnly && tool.layout !== Layout.Blooket && (
                            <ChatSidebar
                                disabled={mustProvideInputs}
                                showChat={showChat}
                                setShowChat={setShowChat}
                            />
                        )}
                    </View>
                </PrefsDrawer>
            )}
            {!chatOnly && <GuideTermsModal />}
        </View>
    );

    if (platform === "Canvas") {
        return (
            <PageRoot
                template={EmbeddedPageTemplate}
                settings={{title: tool.title}}
            >
                <base target="_blank" />
                {aiToolContent}
            </PageRoot>
        );
    }

    return (
        <PageRoot
            template={HeaderOnlyPageTemplate}
            settings={{
                title: tool.title,
                headerBackgroundColor: "darkBlue",
                footer: false,
            }}
        >
            {redirectURL !== "" ? (
                <DelayedRedirect to={redirectURL} delay={3000} />
            ) : (
                aiToolContent
            )}
        </PageRoot>
    );
};

/**
 * This wrapper performs admin-related operations for an AIToolPage component.
 *
 * @param props The external-facing props for the AIToolPage component.
 * @returns The AIToolPage component with data related to the "admin" persona.
 *  */
const AIToolPageAdminWrapper = (props: AIToolPageWrapperProps) => {
    const [adminToolsResult] = useQuery(ADMIN_TOOLS_FETCH);
    const history = useHistory();

    const isLoading = adminToolsResult.status === "loading";
    const isSuccess = adminToolsResult.status === "success";

    const tool = getAdminToolsByUrlSlug()[props.params.tool];

    // Redirect if no tool matches the URL param, or if the user does not have
    // access to the tool.
    if (
        !tool ||
        (isSuccess &&
            !getToolId(
                adminToolsResult?.data?.aiGuideAdminTools,
                tool.configName,
            ))
    ) {
        history.push(ADMIN_KHANMIGO_TOOLS_PATH);
        return null;
    }

    return (
        <AIToolPage
            {...props}
            tool={tool}
            isLoading={isLoading}
            isSuccess={isSuccess}
        />
    );
};

/**
 * This wrapper performs parent-related operations for an AIToolPage component.
 *
 * @param props The external-facing props for the AIToolPage component.
 * @returns The AIToolPage component with data related to the "parent" persona.
 */
const AIToolPageParentWrapper = (props: AIToolPageWrapperProps) => {
    const [parentToolsResult] = useQuery(PARENT_TOOLS_FETCH);
    const history = useHistory();

    const isLoading = parentToolsResult.status === "loading";
    const isSuccess = parentToolsResult.status === "success";

    const daperPhase2FeatureFlag = useFlagOrCookie("tx-daper-phase-2");
    const tool = getParentToolsByUrlSlug(daperPhase2FeatureFlag)[
        props.params.tool
    ];

    // Redirect if no tool matches the URL param, or if the user does not have
    // access to the tool.
    if (
        !tool ||
        (isSuccess &&
            !getToolId(
                parentToolsResult?.data?.aiGuideParentTools,
                tool.configName,
            ))
    ) {
        history.push(PARENT_KHANMIGO_TOOLS_PATH);
        return null;
    }

    return (
        <AIToolPage
            {...props}
            tool={tool}
            isLoading={isLoading}
            isSuccess={isSuccess}
        />
    );
};

/**
 * This wrapper performs teacher-related operations for an AIToolPage component.
 *
 * @param props The external-facing props for the AIToolPage component.
 * @returns The AIToolPage component with data related to the "teacher" persona.
 */
const AIToolPageTeacherWrapper = (props: AIToolPageWrapperProps) => {
    const [teacherToolsResult] = useQuery(TEACHER_TOOLS_FETCH);
    const history = useHistory();

    const isLoading = teacherToolsResult.status === "loading";
    const isSuccess = teacherToolsResult.status === "success";

    const tool = getToolsByUrlSlug()[props.params.tool];

    // Redirect if no tool matches the URL param, or if the user does not have
    // access to the tool.
    if (
        !tool ||
        (isSuccess &&
            !getToolId(
                teacherToolsResult?.data?.aiGuideTeacherTools,
                tool.configName,
            ))
    ) {
        history.push(TEACHER_KHANMIGO_TOOLS_PATH);
        return null;
    }

    return (
        <AIToolPage
            {...props}
            tool={tool}
            isLoading={isLoading}
            isSuccess={isSuccess}
        />
    );
};

export const TOOL_MAX_WIDTH = 800;
export const styles = StyleSheet.create({
    panel: {
        flex: "1 1 auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: "white",
        boxSizing: "border-box",
        height: "100%",
        width: "100%",
        backgroundColor: color.offWhite,
    },
    wrapper: {
        flexGrow: 1,
        flexDirection: "row",
        justifyContent: "space-between",
        position: "relative",
    },
    disclaimer: {
        background: "transparent",
        border: "none",
        height: 60,
    },
    column: {
        display: "block",
        flexGrow: 1,
        overflowY: "auto",
        height: "100%",
        paddingBottom: spacing.xLarge_32,
    },
    mainContent: {
        width: `calc(100% - ${spacing.xLarge_32}px)`,
        maxWidth: TOOL_MAX_WIDTH,
        margin: "0 auto",
    },
    mainContentPaintedContainer: {
        backgroundColor: color.white,
        border: `1px solid ${color.offBlack8}`,
        borderRadius: spacing.xxxSmall_4,
    },
    chatOnlyMainContent: {
        height: `calc(100vh - 280px)`,
    },
    chatOnlyMainContentIframe: {
        height: `calc(100vh - 165px)`,
    },
    coeditorWrapper: {
        minHeight: `calc(100vh - 300px)`,
    },
    loading: {
        minHeight: `calc(100vh - 300px)`,
        alignItems: "center",
        justifyContent: "center",
    },
});

// August 13, 2024
// This wrapper allows LTv2 events to be passed in product data
// when fired in the AIToolPage component
// This is necessary for events fired in the Canvas iFrame because
// AIToolPage does not fall in `CanvasIntegration` wrapper component
const AIToolPageWithContext = (): React.ReactElement => {
    const {queryParams, search} = useLocation();
    const persona = useAIGuideToolPersona();
    const params = useParams<{tool: string}>();
    const platform =
        queryParams.platform === "Canvas"
            ? Platform.Canvas
            : Platform.KhanAcademy;

    return (
        <ConversionContextProvider
            learningTimeInfo={{
                product:
                    platform === Platform.Canvas
                        ? enums.Product.INSTRUCTURE_CANVAS
                        : enums.Product.OTHER_PRODUCT,
                activity: enums.Activity.AI_GUIDE_ACTIVITY,
            }}
        >
            <CoeditorContextProvider>
                {persona === "ADMIN" ? (
                    <AIToolPageAdminWrapper
                        platform={platform}
                        params={params}
                        search={search}
                    />
                ) : persona === "PARENT" ? (
                    <AIToolPageParentWrapper
                        platform={platform}
                        params={params}
                        search={search}
                    />
                ) : (
                    <AIToolPageTeacherWrapper
                        platform={platform}
                        params={params}
                        search={search}
                    />
                )}
            </CoeditorContextProvider>
        </ConversionContextProvider>
    );
};

export default withStrings(AIToolPageWithContext);
